import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Home, X, AlertCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import type { IdentifiedProperty } from './IdentificationPropertyRow'
import {
  canAddProperty,
  type IdentificationRule,
  type PropertyForRules,
} from '../../lib/identification/ruleEnforcement'

const optionalNonNegativeNumber = z.preprocess(
  (value) => (value === '' || value === null || Number.isNaN(value as number) ? undefined : value),
  z.number().nonnegative().optional(),
)

const optionalPercentage = z.preprocess(
  (value) => (value === '' || value === null || Number.isNaN(value as number) ? undefined : value),
  z.number().min(0).max(100).optional(),
)

const likeKindSchema = z.object({
  address: z.string().min(1, 'Address is required'),
  estimatedFMV: z
    .number({ invalid_type_error: 'FMV must be a number' })
    .positive('FMV must be greater than zero'),
  description: z.string().optional(),
  legalDescription: z.string().optional(),
  percentageInterest: optionalPercentage,
  dollarValueAcquired: optionalNonNegativeNumber,
})

type LikeKindFormValues = z.infer<typeof likeKindSchema>

interface LikeKindPropertyFormProps {
  onAdd: (property: IdentifiedProperty) => void
  onCancel: () => void
  rule?: IdentificationRule | ''
  existingProperties?: PropertyForRules[]
  relinquishedFMV?: number | null
}

export function LikeKindPropertyForm({
  onAdd,
  onCancel,
  rule,
  existingProperties = [],
  relinquishedFMV = null,
}: LikeKindPropertyFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<LikeKindFormValues>({
    resolver: zodResolver(likeKindSchema),
    mode: 'onChange',
    defaultValues: {
      address: '',
      estimatedFMV: undefined,
      description: '',
      legalDescription: '',
      percentageInterest: undefined,
      dollarValueAcquired: undefined,
    },
  })

  const watchedFMV = watch('estimatedFMV')
  const candidateFMV = typeof watchedFMV === 'number' && isFinite(watchedFMV) ? watchedFMV : 0
  const ruleCheck = rule
    ? canAddProperty(rule as IdentificationRule, existingProperties, candidateFMV, relinquishedFMV)
    : { canAdd: true, valid: true }
  const ruleBlocked = !ruleCheck.canAdd
  const submitDisabled = !isValid || ruleBlocked

  function onSubmit(values: LikeKindFormValues) {
    onAdd({
      address: values.address,
      estimatedFMV: values.estimatedFMV,
      type: 'like-kind',
      description: values.description || undefined,
      legalDescription: values.legalDescription || undefined,
      percentageInterest: values.percentageInterest,
      dollarValueAcquired: values.dollarValueAcquired,
    })
    reset()
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-3"
      aria-label="Add like-kind property"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
            Add a like-kind property
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          aria-label="Cancel adding like-kind property"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-2">
        <div>
          <label
            htmlFor="likekind-address"
            className="block text-xs font-medium text-foreground/80 mb-1"
          >
            Property address <span className="text-red-500">*</span>
          </label>
          <Input
            id="likekind-address"
            {...register('address')}
            placeholder="123 Main St, City, State ZIP"
            className="text-sm"
            aria-describedby={errors.address ? 'likekind-address-error' : undefined}
            aria-invalid={!!errors.address}
          />
          {errors.address && (
            <p id="likekind-address-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
              {errors.address.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="likekind-fmv"
            className="block text-xs font-medium text-foreground/80 mb-1"
          >
            Estimated FMV <span className="text-red-500">*</span>
          </label>
          <Input
            id="likekind-fmv"
            type="number"
            {...register('estimatedFMV', { valueAsNumber: true })}
            placeholder="$0"
            className="text-sm"
            aria-describedby={errors.estimatedFMV ? 'likekind-fmv-error' : undefined}
            aria-invalid={!!errors.estimatedFMV}
          />
          {errors.estimatedFMV && (
            <p id="likekind-fmv-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
              {errors.estimatedFMV.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="likekind-description"
            className="block text-xs font-medium text-foreground/80 mb-1"
          >
            Description <span className="text-muted-foreground text-[10px]">(optional)</span>
          </label>
          <Input
            id="likekind-description"
            {...register('description')}
            placeholder="e.g. Single-family rental, commercial building"
            className="text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="likekind-legal-description"
            className="block text-xs font-medium text-foreground/80 mb-1"
          >
            Legal Description <span className="text-muted-foreground text-[10px]">(optional — shown on the 45-Day Notice)</span>
          </label>
          <Input
            id="likekind-legal-description"
            {...register('legalDescription')}
            placeholder="Lot 7, Block A, Sample Subdivision, County, State"
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor="likekind-percent-interest"
              className="block text-xs font-medium text-foreground/80 mb-1"
            >
              % Interest <span className="text-muted-foreground text-[10px]">(optional)</span>
            </label>
            <Input
              id="likekind-percent-interest"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register('percentageInterest', { valueAsNumber: true })}
              placeholder="e.g. 100"
              className="text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="likekind-dollar-acquired"
              className="block text-xs font-medium text-foreground/80 mb-1"
            >
              $ Acquired <span className="text-muted-foreground text-[10px]">(optional)</span>
            </label>
            <Input
              id="likekind-dollar-acquired"
              type="number"
              min="0"
              {...register('dollarValueAcquired', { valueAsNumber: true })}
              placeholder="$0"
              className="text-sm"
            />
          </div>
        </div>
      </div>

      {ruleBlocked && ruleCheck.disabledReason && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle
            className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <p className="text-xs text-amber-800 dark:text-amber-200">{ruleCheck.disabledReason}</p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="text-xs"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={submitDisabled}
          title={ruleBlocked ? ruleCheck.disabledReason : undefined}
          className="text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white"
        >
          <Home className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Add Property
        </Button>
      </div>
    </form>
  )
}
